import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BriefcaseBusiness, Check, Pencil, PhoneCall, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import {
  borrarGeneradorPortafolio,
  borrarPermisionario,
  borrarSeguimiento,
  buscarCnePermisos,
  crearGeneradorPortafolio,
  crearPermisionario,
  crearSeguimiento,
  editarGeneradorPortafolio,
  editarPermisionario,
  editarSeguimiento,
  getCatalogoGeneradores,
  getPortafolio,
  getSeguimiento,
  type GeneradorPortafolioIn,
  type MedioContacto,
  type PermisionarioIn,
  type ProductoContacto,
  type ResultadoContacto,
  type ResumenSeguimiento,
  type Seguimiento,
  type SeguimientoIn,
} from "@/lib/api";

type Tipo = "permisionario" | "generador";

const TECNOLOGIAS = [
  "Eólica", "Solar fotovoltaica", "Hidroeléctrica", "Geotérmica", "Ciclo combinado", "Cogeneración eficiente",
  "Nuclear", "Bioenergía", "Turbogás", "Combustión interna", "Otra",
];

const MEDIOS: { v: MedioContacto; l: string }[] = [
  { v: "llamada", l: "Llamada" }, { v: "correo", l: "Correo" }, { v: "reunion", l: "Reunión" },
  { v: "whatsapp", l: "WhatsApp" }, { v: "otro", l: "Otro" },
];
const PRODUCTOS: { v: ProductoContacto; l: string }[] = [
  { v: "ambos", l: "CEL + i-REC" }, { v: "cel", l: "CEL" }, { v: "irec", l: "i-REC" },
];
const RESULTADOS: { v: ResultadoContacto; l: string; cls: string }[] = [
  { v: "pendiente", l: "Pendiente", cls: "bg-slate-100 text-slate-700" },
  { v: "sin_respuesta", l: "Sin respuesta", cls: "bg-amber-50 text-amber-700" },
  { v: "interesado", l: "Interesado", cls: "bg-sky-50 text-sky-700" },
  { v: "oferta_recibida", l: "Oferta recibida", cls: "bg-emerald-50 text-emerald-700" },
  { v: "no_interesado", l: "No interesado", cls: "bg-red-50 text-red-700" },
  { v: "cerrado", l: "Cerrado", cls: "bg-slate-200 text-slate-600" },
];
const etiqueta = <T extends string>(lista: { v: T; l: string }[], v: T) => lista.find((x) => x.v === v)?.l ?? v;
const resultadoCls = (v: ResultadoContacto) => RESULTADOS.find((x) => x.v === v)?.cls ?? "";

const num = (n: number | null, dec = 2) =>
  n == null ? "—" : new Intl.NumberFormat("es-MX", { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n);
const fechaCorta = (iso: string | null) => {
  if (!iso) return "—";
  const [a, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(a, m - 1, d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};
const hoy = () => new Date().toLocaleDateString("en-CA");
const aNum = (s: string): number | null => {
  const t = s.replace(/[,\s]/g, "");
  return t === "" ? null : Number(t);
};
const sinAcentos = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
/** Fecha ISO de hoy menos N años (ventana móvil). */
const hace = (anios: number) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - anios);
  return d.toLocaleDateString("en-CA");
};

const NUEVO = -1;

// ---- Orden por columna (clic en el encabezado: asc → desc → sin orden) ----
type Orden = { col: string; asc: boolean } | null;
type Valor = string | number | boolean | null | undefined;
/** Compara con nulos al final; números y booleanos numéricamente, textos con locale es. */
function comparar(a: Valor, b: Valor): number {
  const va = a == null || a === "" ? null : a;
  const vb = b == null || b === "" ? null : b;
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (typeof va === "number" && typeof vb === "number") return va - vb;
  if (typeof va === "boolean" && typeof vb === "boolean") return Number(vb) - Number(va);
  return String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" });
}
function ordenar<T>(lista: T[], orden: Orden, valor: (x: T, col: string) => Valor): T[] {
  if (!orden) return lista;
  const { col, asc } = orden;
  return [...lista].sort((x, y) => {
    const c = comparar(valor(x, col), valor(y, col));
    // Los nulos siempre al final, sin importar el sentido.
    if (c === 0) return 0;
    const nx = valor(x, col) == null || valor(x, col) === "";
    const ny = valor(y, col) == null || valor(y, col) === "";
    if (nx || ny) return c;
    return asc ? c : -c;
  });
}

const siguienteOrden = (o: Orden, col: string): Orden =>
  o?.col !== col ? { col, asc: true } : o.asc ? { col, asc: false } : null;

/** Encabezado ordenable: con `col` es clicable y muestra ▲ / ▼ cuando es la columna activa. */
function Th({ col, orden, onClick, right, title, children }: {
  col?: string; orden: Orden; onClick: (col: string) => void; right?: boolean; title?: string; children?: ReactNode;
}) {
  const activo = col != null && orden?.col === col;
  return (
    <th className={`px-3 py-2 whitespace-nowrap ${right ? "text-right" : ""} ${col ? "cursor-pointer select-none hover:text-slate-900" : ""} ${activo ? "text-slate-900" : ""}`}
      title={title ?? (col ? "Clic para ordenar" : undefined)} onClick={col ? () => onClick(col) : undefined}>
      {children}{activo && <span className="ml-1 text-[10px]">{orden!.asc ? "▲" : "▼"}</span>}
    </th>
  );
}

type BorradorP = Omit<PermisionarioIn, "capacidad_mw" | "generacion_gwh_anio"> & { capacidad_mw: string; generacion_gwh_anio: string };

// Reglas CEL / i-REC (las mismas que calcula el backend en el listado; aquí
// sirven para la vista previa mientras se edita una ficha):
//   CEL   = LIE · MEM con energía limpia.
//   i-REC = NO es LIE · MEM y la tecnología es eólica o solar (fotovoltaica).
const esLieMem = (b: { marco_legal: string | null; modalidad: string | null }) =>
  (b.marco_legal ?? "").trim().toUpperCase() === "LIE" && (b.modalidad ?? "").trim().toUpperCase() === "MEM";
const esEolicaSolar = (tecnologia: string | null) => /eolic|solar|fotovolt/.test(sinAcentos(tecnologia ?? ""));
const calcCel = (b: BorradorP) => esLieMem(b) && b.es_limpia === true;
const calcIrec = (b: BorradorP) => !esLieMem(b) && esEolicaSolar(b.tecnologia);
type BorradorG = Omit<GeneradorPortafolioIn, "mw"> & { mw: string };

/*
 * Operación → Portafolio CEL / i-REC. Cartera de permisionarios (permisos de
 * generación de la CNE) y de generadores del MEM (claves G### del SIM) a los
 * que se contacta para conocer sus ofertas de CEL e i-REC. Cada ficha tiene un
 * botón de Seguimiento que abre su bitácora de llamadas / correos / reuniones.
 */
export function PortafolioPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["portafolio"], queryFn: getPortafolio });
  // ?tab=permisionario|generador&q=… desde la búsqueda global.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tipo>(
    searchParams.get("tab") === "generador" ? "generador" : "permisionario",
  );
  const [filtro, setFiltro] = useState(searchParams.get("q") ?? "");
  const [verInactivos, setVerInactivos] = useState(false);
  const [soloCel, setSoloCel] = useState(false);
  const [soloIrec, setSoloIrec] = useState(false);
  const [tecno, setTecno] = useState("");            // filtro por tecnología ("" = todas)
  const [ult15, setUlt15] = useState(false);         // entrada en operación en los últimos 15 años (móviles)
  const [ordenP, setOrdenP] = useState<Orden>(null);
  const [ordenG, setOrdenG] = useState<Orden>(null);
  const [error, setError] = useState<string | null>(null);
  const [editP, setEditP] = useState<number | null>(null);
  const [bP, setBP] = useState<BorradorP | null>(null);
  const [editG, setEditG] = useState<number | null>(null);
  const [bG, setBG] = useState<BorradorG | null>(null);
  const [seg, setSeg] = useState<{ tipo: Tipo; id: number; nombre: string } | null>(null);

  const invalidar = () => qc.invalidateQueries({ queryKey: ["portafolio"] });
  const err = (e: unknown, def: string) => setError(e instanceof Error ? e.message : def);

  const guardarP = useMutation({
    mutationFn: ({ id, b }: { id: number; b: BorradorP }) => {
      const p: PermisionarioIn = {
        ...b, capacidad_mw: aNum(b.capacidad_mw), generacion_gwh_anio: aNum(b.generacion_gwh_anio),
        fecha_entrada_operacion: b.fecha_entrada_operacion || null,
      };
      return id === NUEVO ? crearPermisionario(p) : editarPermisionario(id, p);
    },
    onSuccess: () => { setError(null); setEditP(null); setBP(null); invalidar(); },
    onError: (e) => err(e, "No se pudo guardar el permisionario"),
  });
  const borrarP = useMutation({ mutationFn: borrarPermisionario, onSuccess: invalidar, onError: (e) => err(e, "No se pudo borrar") });

  const guardarG = useMutation({
    mutationFn: ({ id, b }: { id: number; b: BorradorG }) => {
      const g: GeneradorPortafolioIn = { ...b, mw: aNum(b.mw) };
      return id === NUEVO ? crearGeneradorPortafolio(g) : editarGeneradorPortafolio(id, g);
    },
    onSuccess: () => { setError(null); setEditG(null); setBG(null); invalidar(); },
    onError: (e) => err(e, "No se pudo guardar el generador"),
  });
  const borrarG = useMutation({ mutationFn: borrarGeneradorPortafolio, onSuccess: invalidar, onError: (e) => err(e, "No se pudo borrar") });

  const t = sinAcentos(filtro.trim());
  const limite15 = hace(15);
  // Tecnologías presentes en la pestaña activa (para el combo del filtro).
  const tecnologias = useMemo(() => {
    const lista = tab === "permisionario" ? (q.data?.permisionarios ?? []) : (q.data?.generadores ?? []);
    return Array.from(new Set(lista.map((x) => (x.tecnologia ?? "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, "es"));
  }, [q.data, tab]);
  const permisionarios = useMemo(() => ordenar((q.data?.permisionarios ?? [])
    .filter((p) => verInactivos || p.activo)
    // CEL / iREC se combinan como UNIÓN: con ambas marcadas salen los que son CEL o iREC.
    .filter((p) => (!soloCel && !soloIrec) || (soloCel && p.cel) || (soloIrec && p.irec))
    .filter((p) => !tecno || (p.tecnologia ?? "").trim() === tecno)
    .filter((p) => !ult15 || (p.fecha_entrada_operacion != null && p.fecha_entrada_operacion.slice(0, 10) >= limite15))
    .filter((p) => !t || sinAcentos(`${p.razon_social} ${p.id_generador ?? ""} ${p.nombre_generador ?? ""} ${p.nombre_central ?? ""} ${p.numero_permiso ?? ""} ${p.tecnologia ?? ""} ${p.entidad ?? ""} ${p.modalidad ?? ""}`).includes(t)),
    ordenP, (p, col) => col === "ultimo" ? p.ultimo?.fecha : (p as unknown as Record<string, Valor>)[col]),
  [q.data, t, verInactivos, soloCel, soloIrec, tecno, ult15, limite15, ordenP]);
  const generadores = useMemo(() => ordenar((q.data?.generadores ?? [])
    .filter((g) => verInactivos || g.activo)
    .filter((g) => !tecno || (g.tecnologia ?? "").trim() === tecno)
    .filter((g) => !t || sinAcentos(`${g.clave} ${g.generador} ${g.centrales_generadoras ?? ""} ${g.nombre_comercial ?? ""} ${g.contacto_comercial ?? ""} ${g.tecnologia ?? ""}`).includes(t)),
    ordenG, (g, col) => col === "ultimo" ? g.ultimo?.fecha : (g as unknown as Record<string, Valor>)[col]),
  [q.data, t, verInactivos, tecno, ordenG]);

  const clicOrdenP = (col: string) => setOrdenP((o) => siguienteOrden(o, col));
  const clicOrdenG = (col: string) => setOrdenG((o) => siguienteOrden(o, col));

  const editando = editP !== null || editG !== null;
  const cancelar = () => { setEditP(null); setBP(null); setEditG(null); setBG(null); };

  const nuevoP = () => {
    setTab("permisionario"); setEditP(NUEVO);
    setBP({ razon_social: "", id_generador: "", nombre_generador: "", nombre_central: "", numero_permiso: "", marco_legal: "",
      modalidad: "", fecha_otorgamiento: null, capacidad_mw: "", generacion_gwh_anio: "", fecha_entrada_operacion: null,
      energetico_primario: "", tecnologia: "", entidad: "", pais_origen: "", es_renovable: null, es_limpia: null, notas: "", activo: true });
  };
  const nuevoG = () => {
    setTab("generador"); setEditG(NUEVO);
    setBG({ clave: "", generador: "", centrales_generadoras: "", mw: "", tecnologia: "", nombre_comercial: "",
      contacto_comercial: "", notas: "", activo: true });
  };

  const pendientes = (q.data?.permisionarios ?? []).filter((p) => p.proximo && p.proximo <= hoy()).length
    + (q.data?.generadores ?? []).filter((g) => g.proximo && g.proximo <= hoy()).length;

  const btnTab = (v: Tipo, label: string, n: number) => (
    <button type="button" onClick={() => { setTab(v); setTecno(""); cancelar(); }}
      className={`px-3 py-1.5 text-sm rounded-t border-b-2 ${tab === v ? "border-slate-800 text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
      {label} <span className="text-xs text-slate-400">({n})</span>
    </button>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <BriefcaseBusiness className="w-5 h-5 text-slate-500" /> Portafolio CEL / i-REC
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Permisionarios y generadores a los que contactamos para conocer sus ofertas de CEL e i-REC. Cada ficha tiene
          un botón de <b>Seguimiento</b> con la bitácora de llamadas, correos y reuniones. Los permisionarios vienen del
          padrón de permisos de generación (ID Generador = clave G### del SIM) y se prellenan desde los permisos de la
          CNE; los generadores, desde el catálogo de participantes del SIM. Las columnas <b>CEL</b> e <b>i-REC</b> se
          calculan solas: CEL = permiso LIE · MEM con energía limpia; i-REC = permiso que no es LIE · MEM y es eólico
          o solar.
          {pendientes > 0 && <span className="text-red-600"> Hay {pendientes} contacto(s) con seguimiento vencido o para hoy.</span>}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm flex justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-700"><X className="w-4 h-4" /></button>
        </div>
      )}
      {q.isLoading && <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>}
      {q.isError && <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">{(q.error as Error).message}</div>}

      {q.data && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-2 border-b">
            <div className="flex gap-1">
              {btnTab("permisionario", "Permisionarios", q.data.permisionarios.length)}
              {btnTab("generador", "Generadores", q.data.generadores.length)}
            </div>
            <div className="flex items-center gap-3 pb-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar…"
                  className="border rounded pl-8 pr-2 py-1.5 text-sm w-56" />
              </div>
              <select value={tecno} onChange={(e) => setTecno(e.target.value)} title="Filtrar por tecnología"
                className="border rounded px-2 py-1.5 text-sm max-w-[200px]">
                <option value="">Todas las tecnologías</option>
                {tecnologias.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              {tab === "permisionario" && (
                <>
                  <label className="inline-flex items-center gap-1 text-xs text-slate-600" title="Con CEL e iREC marcados se muestran los que son CEL o iREC">
                    <input type="checkbox" checked={soloCel} onChange={(e) => setSoloCel(e.target.checked)} /> CEL
                  </label>
                  <label className="inline-flex items-center gap-1 text-xs text-slate-600" title="Con CEL e iREC marcados se muestran los que son CEL o iREC">
                    <input type="checkbox" checked={soloIrec} onChange={(e) => setSoloIrec(e.target.checked)} /> iREC
                  </label>
                  <label className="inline-flex items-center gap-1 text-xs text-slate-600"
                    title={`Fecha de entrada en operación desde ${fechaCorta(limite15)} (15 años móviles)`}>
                    <input type="checkbox" checked={ult15} onChange={(e) => setUlt15(e.target.checked)} /> Entrada en operación ≤ 15 años
                  </label>
                </>
              )}
              <label className="inline-flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} /> Ver inactivos
              </label>
              <button type="button" onClick={tab === "permisionario" ? nuevoP : nuevoG} disabled={editando}
                className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                <Plus className="w-4 h-4" /> Agregar {tab === "permisionario" ? "permisionario" : "generador"}
              </button>
            </div>
          </div>

          {tab === "permisionario" ? (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left text-xs">
                <tr>
                  <Th col="razon_social" orden={ordenP} onClick={clicOrdenP}>Permisionario (Razón Social)</Th>
                  <Th col="id_generador" orden={ordenP} onClick={clicOrdenP}>ID Generador</Th>
                  <Th col="nombre_generador" orden={ordenP} onClick={clicOrdenP}>Nombre del Generador</Th>
                  <Th col="numero_permiso" orden={ordenP} onClick={clicOrdenP}>Nº Permiso</Th>
                  <Th col="capacidad_mw" orden={ordenP} onClick={clicOrdenP} right>Capacidad Autorizada (MW)</Th>
                  <Th col="generacion_gwh_anio" orden={ordenP} onClick={clicOrdenP} right>Generación Estimada (GWh/Año)</Th>
                  <Th col="fecha_entrada_operacion" orden={ordenP} onClick={clicOrdenP}>Fecha Entrada Operación</Th>
                  <Th col="tecnologia" orden={ordenP} onClick={clicOrdenP}>Tecnología</Th>
                  <Th col="cel" orden={ordenP} onClick={clicOrdenP} title="LIE · MEM con energía limpia · clic para ordenar">CEL</Th>
                  <Th col="irec" orden={ordenP} onClick={clicOrdenP} title="No LIE · MEM y eólica o solar · clic para ordenar">iREC</Th>
                  <Th col="ultimo" orden={ordenP} onClick={clicOrdenP}>Último contacto</Th>
                  <Th col="proximo" orden={ordenP} onClick={clicOrdenP}>Próximo</Th>
                  <th className="px-3 py-2 w-28" />
                </tr>
              </thead>
              <tbody>
                {editP === NUEVO && bP && (
                  <FilaPermisionario b={bP} onChange={setBP} ocupado={guardarP.isPending}
                    onGuardar={() => guardarP.mutate({ id: NUEVO, b: bP })} onCancelar={cancelar} />
                )}
                {permisionarios.map((p) => editP === p.id && bP ? (
                  <FilaPermisionario key={p.id} b={bP} onChange={setBP} ocupado={guardarP.isPending}
                    onGuardar={() => guardarP.mutate({ id: p.id, b: bP })} onCancelar={cancelar} />
                ) : (
                  <tr key={p.id} className={`border-t hover:bg-slate-50 ${p.activo ? "" : "text-slate-400"}`}>
                    <td className="px-3 py-1.5 font-medium">
                      {p.razon_social}
                      {(p.entidad || p.pais_origen) && <div className="text-[11px] text-slate-400 font-normal">{[p.entidad, p.pais_origen].filter(Boolean).join(" · ")}</div>}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{p.id_generador ?? "—"}</td>
                    <td className="px-3 py-1.5">
                      {p.nombre_generador ?? "—"}
                      {p.nombre_central && <div className="text-[11px] text-slate-500">{p.nombre_central}</div>}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap" title={[p.marco_legal, p.modalidad, p.fecha_otorgamiento ? `otorgado ${fechaCorta(p.fecha_otorgamiento)}` : ""].filter(Boolean).join(" · ")}>
                      {p.numero_permiso ?? "—"}
                      {p.modalidad && <div className="text-[11px] text-slate-400 font-sans">{p.marco_legal} · {p.modalidad}</div>}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(p.capacidad_mw, 1)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(p.generacion_gwh_anio, 1)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{fechaCorta(p.fecha_entrada_operacion)}</td>
                    <td className="px-3 py-1.5">
                      {p.tecnologia ?? "—"}
                      {p.energetico_primario && <div className="text-[11px] text-slate-400">{p.energetico_primario}</div>}
                    </td>
                    <td className="px-3 py-1.5" title={p.es_limpia == null ? "Energía limpia por revisar" : p.es_limpia ? "Energía limpia" : "No es energía limpia"}>
                      <BadgeSiNo v={p.cel} />
                    </td>
                    <td className="px-3 py-1.5"><BadgeSiNo v={p.irec} /></td>
                    <CeldasSeguimiento r={p} />
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <BotonesFila nombre={p.razon_social} editando={editando}
                        onSeg={() => setSeg({ tipo: "permisionario", id: p.id, nombre: p.razon_social })}
                        onEditar={() => { setEditP(p.id); setBP({ ...p, id_generador: p.id_generador ?? "", nombre_generador: p.nombre_generador ?? "",
                          nombre_central: p.nombre_central ?? "", numero_permiso: p.numero_permiso ?? "", marco_legal: p.marco_legal ?? "",
                          modalidad: p.modalidad ?? "", energetico_primario: p.energetico_primario ?? "", entidad: p.entidad ?? "",
                          pais_origen: p.pais_origen ?? "",
                          capacidad_mw: p.capacidad_mw == null ? "" : String(p.capacidad_mw),
                          generacion_gwh_anio: p.generacion_gwh_anio == null ? "" : String(p.generacion_gwh_anio),
                          tecnologia: p.tecnologia ?? "", notas: p.notas ?? "" }); }}
                        onBorrar={() => borrarP.mutate(p.id)} />
                    </td>
                  </tr>
                ))}
                {permisionarios.length === 0 && editP !== NUEVO && (
                  <tr><td colSpan={13} className="px-3 py-6 text-center text-slate-500">Sin permisionarios.</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left text-xs">
                <tr>
                  <Th col="clave" orden={ordenG} onClick={clicOrdenG}>Clave</Th>
                  <Th col="generador" orden={ordenG} onClick={clicOrdenG}>Generador</Th>
                  <Th col="centrales_generadoras" orden={ordenG} onClick={clicOrdenG}>Centrales Generadoras</Th>
                  <Th col="mw" orden={ordenG} onClick={clicOrdenG} right>MW</Th>
                  <Th col="tecnologia" orden={ordenG} onClick={clicOrdenG}>Tecnología</Th>
                  <Th col="nombre_comercial" orden={ordenG} onClick={clicOrdenG}>Nombre Comercial</Th>
                  <Th col="contacto_comercial" orden={ordenG} onClick={clicOrdenG}>Contacto Comercial</Th>
                  <Th col="ultimo" orden={ordenG} onClick={clicOrdenG}>Último contacto</Th>
                  <Th col="proximo" orden={ordenG} onClick={clicOrdenG}>Próximo</Th>
                  <th className="px-3 py-2 w-28" />
                </tr>
              </thead>
              <tbody>
                {editG === NUEVO && bG && (
                  <FilaGenerador b={bG} onChange={setBG} ocupado={guardarG.isPending}
                    onGuardar={() => guardarG.mutate({ id: NUEVO, b: bG })} onCancelar={cancelar} />
                )}
                {generadores.map((g) => editG === g.id && bG ? (
                  <FilaGenerador key={g.id} b={bG} onChange={setBG} ocupado={guardarG.isPending}
                    onGuardar={() => guardarG.mutate({ id: g.id, b: bG })} onCancelar={cancelar} />
                ) : (
                  <tr key={g.id} className={`border-t hover:bg-slate-50 ${g.activo ? "" : "text-slate-400"}`}>
                    <td className="px-3 py-1.5 font-mono text-xs">{g.clave}</td>
                    <td className="px-3 py-1.5 font-medium">{g.generador}</td>
                    <td className="px-3 py-1.5 whitespace-pre-line">{g.centrales_generadoras ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(g.mw)}</td>
                    <td className="px-3 py-1.5">{g.tecnologia ?? "—"}</td>
                    <td className="px-3 py-1.5">{g.nombre_comercial ?? "—"}</td>
                    <td className="px-3 py-1.5 whitespace-pre-line">{g.contacto_comercial ?? "—"}</td>
                    <CeldasSeguimiento r={g} />
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <BotonesFila nombre={`${g.clave} ${g.generador}`} editando={editando}
                        onSeg={() => setSeg({ tipo: "generador", id: g.id, nombre: `${g.clave} · ${g.generador}` })}
                        onEditar={() => { setEditG(g.id); setBG({ ...g, centrales_generadoras: g.centrales_generadoras ?? "",
                          mw: g.mw == null ? "" : String(g.mw), tecnologia: g.tecnologia ?? "", nombre_comercial: g.nombre_comercial ?? "",
                          contacto_comercial: g.contacto_comercial ?? "", notas: g.notas ?? "" }); }}
                        onBorrar={() => borrarG.mutate(g.id)} />
                    </td>
                  </tr>
                ))}
                {generadores.length === 0 && editG !== NUEVO && (
                  <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-500">Sin generadores.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {seg && <PanelSeguimiento tipo={seg.tipo} id={seg.id} nombre={seg.nombre} onCerrar={() => { setSeg(null); invalidar(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Celdas y botones comunes a ambas tablas
// ---------------------------------------------------------------------------

function BadgeSiNo({ v }: { v: boolean }) {
  return v
    ? <span className="px-1.5 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700">Sí</span>
    : <span className="px-1.5 py-0.5 rounded text-[11px] bg-slate-100 text-slate-500">No</span>;
}

function CeldasSeguimiento({ r }: { r: ResumenSeguimiento }) {
  const vencido = r.proximo != null && r.proximo <= hoy();
  return (
    <>
      <td className="px-3 py-1.5 max-w-[260px]">
        {r.ultimo ? (
          <div>
            <div className="text-xs whitespace-nowrap">
              <span className="tabular-nums">{fechaCorta(r.ultimo.fecha)}</span> · {etiqueta(MEDIOS, r.ultimo.medio)}
              <span className={`ml-1 px-1.5 py-0.5 rounded text-[11px] ${resultadoCls(r.ultimo.resultado)}`}>{etiqueta(RESULTADOS, r.ultimo.resultado)}</span>
            </div>
            <div className="text-xs text-slate-500 truncate" title={r.ultimo.resumen}>{r.ultimo.resumen}</div>
          </div>
        ) : <span className="text-slate-400 text-xs">Sin contactos</span>}
      </td>
      <td className={`px-3 py-1.5 whitespace-nowrap tabular-nums text-xs ${vencido ? "text-red-600 font-semibold" : ""}`}>
        {fechaCorta(r.proximo)}
      </td>
    </>
  );
}

function BotonesFila({ nombre, editando, onSeg, onEditar, onBorrar }: {
  nombre: string; editando: boolean; onSeg: () => void; onEditar: () => void; onBorrar: () => void;
}) {
  return (
    <>
      <button type="button" onClick={onSeg} title="Seguimiento: bitácora de llamadas y contactos"
        className="inline-flex items-center gap-1 border rounded px-2 py-0.5 text-xs text-slate-700 hover:bg-sky-50 hover:border-sky-300 mr-2">
        <PhoneCall className="w-3.5 h-3.5" /> Seguimiento
      </button>
      <button type="button" title="Modificar" disabled={editando} onClick={onEditar} className="text-slate-400 hover:text-blue-600 disabled:opacity-30 mr-2">
        <Pencil className="w-4 h-4" />
      </button>
      <button type="button" title="Eliminar (y su bitácora)" disabled={editando}
        onClick={() => { if (window.confirm(`¿Eliminar "${nombre}" y toda su bitácora de seguimiento?`)) onBorrar(); }}
        className="text-slate-400 hover:text-red-600 disabled:opacity-30">
        <Trash2 className="w-4 h-4" />
      </button>
    </>
  );
}

const inp = "border rounded px-1.5 py-1 text-sm w-full";

function Acciones({ ocupado, onGuardar, onCancelar }: { ocupado: boolean; onGuardar: () => void; onCancelar: () => void }) {
  return (
    <td className="px-2 py-1.5 whitespace-nowrap">
      <button type="button" title="Guardar (Enter)" disabled={ocupado} onClick={onGuardar} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40 mr-2">
        <Check className="w-4 h-4" />
      </button>
      <button type="button" title="Cancelar (Esc)" onClick={onCancelar} className="text-slate-400 hover:text-slate-700">
        <X className="w-4 h-4" />
      </button>
    </td>
  );
}

const teclas = (onGuardar: () => void, onCancelar: () => void) => (e: KeyboardEvent) => {
  if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) onGuardar();
  if (e.key === "Escape") onCancelar();
};

function SelectTecnologia({ valor, onChange }: { valor: string; onChange: (v: string) => void }) {
  return (
    <>
      <input list="tecnologias" value={valor} onChange={(e) => onChange(e.target.value)} placeholder="Tecnología" className={inp} />
      <datalist id="tecnologias">{TECNOLOGIAS.map((x) => <option key={x} value={x} />)}</datalist>
    </>
  );
}

// ---------------------------------------------------------------------------
// Fila editable de permisionario (con búsqueda en los permisos de la CNE)
// ---------------------------------------------------------------------------

function FilaPermisionario({ b, onChange, ocupado, onGuardar, onCancelar }: {
  b: BorradorP; onChange: (b: BorradorP) => void; ocupado: boolean; onGuardar: () => void; onCancelar: () => void;
}) {
  const set = <K extends keyof BorradorP>(k: K, v: BorradorP[K]) => onChange({ ...b, [k]: v });
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState(b.razon_social);
  const [consulta, setConsulta] = useState("");
  const caja = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    const h = setTimeout(() => setConsulta(texto.trim()), 350);
    return () => clearTimeout(h);
  }, [texto]);
  useEffect(() => {
    const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);
  const cne = useQuery({
    queryKey: ["cne-permisos", consulta],
    queryFn: () => buscarCnePermisos(consulta),
    enabled: abierto && consulta.length >= 3,
    staleTime: 60_000,
  });

  return (
    <tr className="border-t bg-blue-50/60 align-top" onKeyDown={teclas(onGuardar, onCancelar)}>
      <td ref={caja} className="px-2 py-1.5 min-w-[240px] relative">
        <input autoFocus value={texto} placeholder="Razón social (busca en permisos CNE)"
          onChange={(e) => { setTexto(e.target.value); set("razon_social", e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)} className={inp} />
        {abierto && consulta.length >= 3 && (
          <ul className="absolute z-20 mt-1 left-2 w-[520px] max-h-72 overflow-auto rounded border border-slate-200 bg-white shadow-lg text-sm">
            {cne.isFetching && <li className="px-3 py-2 text-slate-400 text-xs">Buscando en permisos CNE…</li>}
            {!cne.isFetching && (cne.data?.length ?? 0) === 0 && (
              <li className="px-3 py-2 text-slate-400 text-xs">Sin coincidencias en permisos de generación de la CNE; captura a mano.</li>
            )}
            {(cne.data ?? []).map((c) => (
              <li key={c.numero}>
                <button type="button" className="w-full text-left px-3 py-1.5 hover:bg-slate-50"
                  onClick={() => {
                    const alias = c.alias_proyecto && c.alias_proyecto !== "-" && c.alias_proyecto !== c.permisionario ? c.alias_proyecto : "";
                    onChange({ ...b, razon_social: c.permisionario, numero_permiso: c.numero, nombre_generador: b.nombre_generador || alias });
                    setTexto(c.permisionario); setAbierto(false);
                  }}>
                  <span className="font-mono text-xs text-slate-500 mr-2">{c.numero}</span>
                  <span className="text-slate-800">{c.permisionario}</span>
                  {c.estatus && <span className="ml-2 text-[11px] text-slate-400">{c.estatus}</span>}
                  {c.alias_proyecto && c.alias_proyecto !== "-" && c.alias_proyecto !== c.permisionario && (
                    <div className="text-xs text-slate-500 truncate">{c.alias_proyecto}</div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-2 py-1.5 w-24"><input value={b.id_generador ?? ""} placeholder="G002" onChange={(e) => set("id_generador", e.target.value.toUpperCase())} className={`${inp} font-mono text-xs`} /></td>
      <td className="px-2 py-1.5 min-w-[160px] space-y-1">
        <input value={b.nombre_generador ?? ""} placeholder="Nombre del generador" onChange={(e) => set("nombre_generador", e.target.value)} className={inp} />
        <input value={b.nombre_central ?? ""} placeholder="Central" onChange={(e) => set("nombre_central", e.target.value)} className={inp} />
      </td>
      <td className="px-2 py-1.5 w-44 space-y-1">
        <input value={b.numero_permiso ?? ""} placeholder="E/1234/GEN/2018" onChange={(e) => set("numero_permiso", e.target.value)} className={`${inp} font-mono text-xs`} />
        <div className="flex gap-1">
          <input value={b.marco_legal ?? ""} placeholder="LIE" onChange={(e) => set("marco_legal", e.target.value)} className={`${inp} text-xs`} />
          <input value={b.modalidad ?? ""} placeholder="MEM" onChange={(e) => set("modalidad", e.target.value)} className={`${inp} text-xs`} />
        </div>
      </td>
      <td className="px-2 py-1.5 w-28"><input inputMode="decimal" value={b.capacidad_mw} onChange={(e) => set("capacidad_mw", e.target.value)} className={`${inp} text-right`} /></td>
      <td className="px-2 py-1.5 w-28"><input inputMode="decimal" value={b.generacion_gwh_anio} onChange={(e) => set("generacion_gwh_anio", e.target.value)} className={`${inp} text-right`} /></td>
      <td className="px-2 py-1.5"><input type="date" value={b.fecha_entrada_operacion ?? ""} onChange={(e) => set("fecha_entrada_operacion", e.target.value || null)} className={inp} /></td>
      <td className="px-2 py-1.5 min-w-[140px] space-y-1">
        <SelectTecnologia valor={b.tecnologia ?? ""} onChange={(v) => set("tecnologia", v)} />
        <input value={b.energetico_primario ?? ""} placeholder="Energético primario" onChange={(e) => set("energetico_primario", e.target.value)} className={`${inp} text-xs`} />
      </td>
      <td className="px-2 py-1.5 w-28">
        <label className="block text-[10px] text-slate-500">Energía limpia</label>
        <select value={b.es_limpia == null ? "" : b.es_limpia ? "1" : "0"} onChange={(e) => set("es_limpia", e.target.value === "" ? null : e.target.value === "1")} className={inp} title="Energía limpia (cuenta para CEL si el permiso es LIE · MEM)">
          <option value="">?</option><option value="1">Sí</option><option value="0">No</option>
        </select>
        <div className="mt-1 text-[11px] text-slate-500">CEL: <BadgeSiNo v={calcCel(b)} /></div>
      </td>
      <td className="px-2 py-1.5 w-16 text-[11px] text-slate-500" title="Se calcula: no LIE · MEM y eólica o solar">
        <BadgeSiNo v={calcIrec(b)} />
      </td>
      <td className="px-2 py-1.5" colSpan={2}>
        <div className="flex gap-1 mb-1">
          <input value={b.entidad ?? ""} placeholder="Entidad" onChange={(e) => set("entidad", e.target.value)} className={`${inp} text-xs`} />
          <input value={b.pais_origen ?? ""} placeholder="País" onChange={(e) => set("pais_origen", e.target.value)} className={`${inp} text-xs`} />
        </div>
        <input value={b.notas ?? ""} placeholder="Notas" onChange={(e) => set("notas", e.target.value)} className={`${inp} mb-1`} />
        <label className="inline-flex items-center gap-1 text-xs">
          <input type="checkbox" checked={b.activo} onChange={(e) => set("activo", e.target.checked)} /> Activo
        </label>
      </td>
      <Acciones ocupado={ocupado} onGuardar={onGuardar} onCancelar={onCancelar} />
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Fila editable de generador (clave del catálogo del SIM)
// ---------------------------------------------------------------------------

function FilaGenerador({ b, onChange, ocupado, onGuardar, onCancelar }: {
  b: BorradorG; onChange: (b: BorradorG) => void; ocupado: boolean; onGuardar: () => void; onCancelar: () => void;
}) {
  const set = <K extends keyof BorradorG>(k: K, v: BorradorG[K]) => onChange({ ...b, [k]: v });
  const cat = useQuery({ queryKey: ["catalogo-generadores"], queryFn: getCatalogoGeneradores, staleTime: 300_000 });
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const caja = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);
  const filtradas = useMemo(() => {
    const t = sinAcentos(texto.trim());
    const lista = cat.data ?? [];
    return (t ? lista.filter((o) => sinAcentos(`${o.clave} ${o.razon_social}`).includes(t)) : lista).slice(0, 60);
  }, [cat.data, texto]);

  return (
    <tr className="border-t bg-blue-50/60 align-top" onKeyDown={teclas(onGuardar, onCancelar)}>
      <td ref={caja} className="px-2 py-1.5 w-28 relative">
        <input autoFocus value={abierto ? texto : b.clave} placeholder="G002"
          onFocus={() => { setAbierto(true); setTexto(""); }}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); set("clave", e.target.value.toUpperCase()); }}
          className={`${inp} font-mono`} />
        {abierto && (
          <ul className="absolute z-20 mt-1 left-2 w-[480px] max-h-72 overflow-auto rounded border border-slate-200 bg-white shadow-lg text-sm">
            {cat.isLoading && <li className="px-3 py-2 text-slate-400 text-xs">Cargando catálogo del SIM…</li>}
            {!cat.isLoading && filtradas.length === 0 && (
              <li className="px-3 py-2 text-slate-400 text-xs">Sin coincidencias; escribe la clave y la razón social a mano.</li>
            )}
            {filtradas.map((o) => (
              <li key={o.clave}>
                <button type="button" disabled={o.en_portafolio}
                  onClick={() => { onChange({ ...b, clave: o.clave, generador: b.generador || o.razon_social }); setAbierto(false); }}
                  className="w-full text-left px-3 py-1.5 hover:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed">
                  <span className="font-mono text-xs text-slate-500 mr-2">{o.clave}</span>
                  <span>{o.razon_social}</span>
                  {o.en_portafolio && <span className="ml-2 text-[11px] text-slate-400">ya en el portafolio</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-2 py-1.5 min-w-[220px]"><input value={b.generador} placeholder="Razón social" onChange={(e) => set("generador", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5 min-w-[180px]"><textarea rows={2} value={b.centrales_generadoras ?? ""} placeholder="Una central por línea" onChange={(e) => set("centrales_generadoras", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5 w-24"><input inputMode="decimal" value={b.mw} onChange={(e) => set("mw", e.target.value)} className={`${inp} text-right`} /></td>
      <td className="px-2 py-1.5 min-w-[140px]"><SelectTecnologia valor={b.tecnologia ?? ""} onChange={(v) => set("tecnologia", v)} /></td>
      <td className="px-2 py-1.5 min-w-[120px]"><input value={b.nombre_comercial ?? ""} onChange={(e) => set("nombre_comercial", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5 min-w-[180px]"><textarea rows={2} value={b.contacto_comercial ?? ""} placeholder="Nombre / correo / teléfono" onChange={(e) => set("contacto_comercial", e.target.value)} className={inp} /></td>
      <td className="px-2 py-1.5" colSpan={2}>
        <input value={b.notas ?? ""} placeholder="Notas" onChange={(e) => set("notas", e.target.value)} className={`${inp} mb-1`} />
        <label className="inline-flex items-center gap-1 text-xs">
          <input type="checkbox" checked={b.activo} onChange={(e) => set("activo", e.target.checked)} /> Activo
        </label>
      </td>
      <Acciones ocupado={ocupado} onGuardar={onGuardar} onCancelar={onCancelar} />
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Panel lateral: bitácora de seguimiento de una ficha
// ---------------------------------------------------------------------------

type BorradorS = Omit<SeguimientoIn, "tipo" | "id_ref">;
const vacioS = (): BorradorS => ({
  fecha: hoy(), medio: "llamada", persona: "", producto: "ambos", resultado: "pendiente", resumen: "",
  siguiente_paso: "", fecha_siguiente: null,
});

function PanelSeguimiento({ tipo, id, nombre, onCerrar }: { tipo: Tipo; id: number; nombre: string; onCerrar: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seguimiento", tipo, id], queryFn: () => getSeguimiento(tipo, id) });
  const [edit, setEdit] = useState<number | null>(NUEVO);
  const [b, setB] = useState<BorradorS>(vacioS());
  const [error, setError] = useState<string | null>(null);
  const invalidar = () => qc.invalidateQueries({ queryKey: ["seguimiento", tipo, id] });

  const guardar = useMutation({
    mutationFn: () => {
      const s: SeguimientoIn = { ...b, tipo, id_ref: id, persona: b.persona || null, siguiente_paso: b.siguiente_paso || null,
        fecha_siguiente: b.fecha_siguiente || null };
      return edit === NUEVO || edit === null ? crearSeguimiento(s) : editarSeguimiento(edit, s);
    },
    onSuccess: () => { setError(null); setEdit(NUEVO); setB(vacioS()); invalidar(); },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo guardar"),
  });
  const borrar = useMutation({ mutationFn: borrarSeguimiento, onSuccess: invalidar,
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar") });

  useEffect(() => {
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onCerrar]);

  const set = <K extends keyof BorradorS>(k: K, v: BorradorS[K]) => setB({ ...b, [k]: v });
  const editarFila = (s: Seguimiento) => {
    setEdit(s.id);
    setB({ fecha: s.fecha, medio: s.medio, persona: s.persona ?? "", producto: s.producto, resultado: s.resultado,
      resumen: s.resumen, siguiente_paso: s.siguiente_paso ?? "", fecha_siguiente: s.fecha_siguiente });
  };
  const sel = "border rounded px-1.5 py-1 text-sm";

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onCerrar} />
      <aside className="relative w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
              <PhoneCall className="w-3.5 h-3.5" /> Seguimiento · {tipo === "permisionario" ? "Permisionario" : "Generador"}
            </div>
            <div className="font-semibold text-slate-900">{nombre}</div>
          </div>
          <button type="button" onClick={onCerrar} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b bg-slate-50 space-y-2">
          <div className="text-xs font-semibold text-slate-600">{edit === NUEVO ? "Registrar contacto" : "Modificar contacto"}</div>
          <div className="grid grid-cols-3 gap-2">
            <input type="date" value={b.fecha} onChange={(e) => set("fecha", e.target.value)} className={sel} />
            <select value={b.medio} onChange={(e) => set("medio", e.target.value as MedioContacto)} className={sel}>
              {MEDIOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <select value={b.producto} onChange={(e) => set("producto", e.target.value as ProductoContacto)} className={sel}>
              {PRODUCTOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={b.persona ?? ""} placeholder="Con quién (nombre / puesto)" onChange={(e) => set("persona", e.target.value)} className={sel} />
            <select value={b.resultado} onChange={(e) => set("resultado", e.target.value as ResultadoContacto)} className={sel}>
              {RESULTADOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <textarea rows={3} value={b.resumen} placeholder="Qué se habló: volumen, precio, vigencia, condiciones…"
            onChange={(e) => set("resumen", e.target.value)} className={`${sel} w-full`} />
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input value={b.siguiente_paso ?? ""} placeholder="Siguiente paso" onChange={(e) => set("siguiente_paso", e.target.value)} className={sel} />
            <input type="date" title="Fecha del próximo contacto" value={b.fecha_siguiente ?? ""} onChange={(e) => set("fecha_siguiente", e.target.value || null)} className={sel} />
          </div>
          {error && <div className="text-xs text-red-600">{error}</div>}
          <div className="flex gap-2 justify-end">
            {edit !== NUEVO && (
              <button type="button" onClick={() => { setEdit(NUEVO); setB(vacioS()); }} className="border rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-white">Cancelar</button>
            )}
            <button type="button" disabled={guardar.isPending || !b.resumen.trim()} onClick={() => guardar.mutate()}
              className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50">
              <Check className="w-4 h-4" /> {edit === NUEVO ? "Registrar" : "Guardar cambios"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-3 space-y-3">
          {q.isLoading && <div className="text-sm text-slate-500">Cargando bitácora…</div>}
          {q.isError && <div className="text-sm text-red-600">{(q.error as Error).message}</div>}
          {q.data?.length === 0 && <div className="text-sm text-slate-500">Aún no hay contactos registrados.</div>}
          {(q.data ?? []).map((s) => {
            const vencido = s.fecha_siguiente != null && s.fecha_siguiente <= hoy() && !["cerrado", "no_interesado"].includes(s.resultado);
            return (
              <div key={s.id} className={`border rounded-lg p-3 ${edit === s.id ? "border-sky-400 bg-sky-50/40" : ""}`}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="tabular-nums font-medium">{fechaCorta(s.fecha)}</span>
                    <span className="text-slate-500">{etiqueta(MEDIOS, s.medio)} · {etiqueta(PRODUCTOS, s.producto)}</span>
                    <span className={`px-1.5 py-0.5 rounded ${resultadoCls(s.resultado)}`}>{etiqueta(RESULTADOS, s.resultado)}</span>
                  </div>
                  <div className="whitespace-nowrap">
                    <button type="button" title="Modificar" onClick={() => editarFila(s)} className="text-slate-400 hover:text-blue-600 mr-2"><Pencil className="w-3.5 h-3.5" /></button>
                    <button type="button" title="Eliminar" onClick={() => { if (window.confirm("¿Eliminar este registro de la bitácora?")) borrar.mutate(s.id); }}
                      className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {s.persona && <div className="text-xs text-slate-600 mt-1">Con: {s.persona}</div>}
                <div className="text-sm text-slate-800 mt-1 whitespace-pre-line">{s.resumen}</div>
                {(s.siguiente_paso || s.fecha_siguiente) && (
                  <div className={`text-xs mt-2 ${vencido ? "text-red-600" : "text-slate-600"}`}>
                    <b>Siguiente:</b> {s.siguiente_paso ?? "contacto"}{s.fecha_siguiente && <> · {fechaCorta(s.fecha_siguiente)}{vencido && " (vencido)"}</>}
                  </div>
                )}
                <div className="text-[11px] text-slate-400 mt-1">{s.registrado_por ?? "—"}</div>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
